import React, { useCallback, useState } from 'react';
import cn from 'clsx';

import Button from '../../../../library/button/button';
import Collapsible from '../../../../library/collapsible/collapsible';
import Image from '../../../../library/image/image';
import Link from '../../../../library/link/link';
import Typography from '../../../../library/typography/typography';
import VehicleDescription from '../../../../library/vehicle-description/vehicle-description';
import VehicleLicenseVinDetails from '../../../../library/vehicle-license-vin-details/vehicle-license-vin-details';
import FitmentSize from '../../../../components/fitment-size/fitment-size';
import TreadwellPersonaIcon from '../../../../components/treadwell-persona-icon/treadwell-persona-icon';
import VehicleImage from '../../../../components/vehicle-image/vehicle-image';
import CollapsibleToggle from '../../../../library/collapsible/components/collapsible-toggle/collapsible-toggle';
import WinterToggle from '../winter-toggle/winter-toggle';

import {
  useFitment,
  useMyAccount,
  useNavigateTo,
  useTireWheelPackage,
  useTreadwellPreferences
} from '../../../../shared/hooks/hooks';

import { trackAction } from '../../../../shared/utils/analytics/analytics';
import { getIsAppBasedDriver } from '../../../../shared/utils/treadwell/treadwell';

import styles from './product-list-driving-profile.scss';

interface ProductListDrivingProfileProps {
  onWinterToggle: () => void;
  showTreadwellProfile: boolean;
}

function ProductListDrivingProfile(props: ProductListDrivingProfileProps) {
  const { onWinterToggle, showTreadwellProfile = false } = props;

  const [isOpen, setIsOpen] = useState(true);

  const { partialFitment, selectedFitment } = useFitment();
  const { treadwellPreferences } = useTreadwellPreferences();
  const { confirmCancelTireWheelPackage, incompletePackage } =
    useTireWheelPackage();
  const { isFleetAccount, user } = useMyAccount();
  const navigateTo = useNavigateTo();

  const isAppBasedDriver = getIsAppBasedDriver();
  const { selectedDriverType, zipCodeWeatherData } = treadwellPreferences || {};
  const { icon, name } = selectedDriverType || {};
  const { city, state } = zipCodeWeatherData || {};

  const fitment = selectedFitment || partialFitment;
  const {
    frontTireSize,
    frontWheelDiameter,
    isOE,
    isStaggered,
    make,
    model,
    rearTireSize,
    rearWheelDiameter,
    rideHeight,
    sizeQualifier,
    smallImageUrl,
    trim,
    year
  } = fitment || {};

  const isFleetEnabled =
    process.env.__FF_FLEET_ACCOUNT__ === 'true' && isFleetAccount;
  const isFleetTreadwell =
    process.env.__FF_FLEET_TREADWELL__ === 'true' && isFleetEnabled;

  const driverValue = name || 'Custom';
  const companyDriverValue = user?.companyName;

  const handleEditVehicle = useCallback(async () => {
    if (incompletePackage) {
      await confirmCancelTireWheelPackage();
    } else {
      navigateTo('#/fitment/vehicle/results');
    }
  }, [confirmCancelTireWheelPackage, incompletePackage, navigateTo]);

  return (
    <div
      className={cn(
        styles['container'],
        isStaggered ? styles['staggered-fitment'] : styles['standard-fitment']
      )}
    >
      {fitment ? (
        <>
          <header className={styles['heading']}>
            {showTreadwellProfile ? (
              <CollapsibleToggle
                className={styles['treadwell-title']}
                isOpen={isOpen}
                label={
                  <>
                    <Image
                      alt="Treadwell"
                      className={styles['treadwell-icon']}
                      height="24px"
                      src="https://discounttire.scene7.com/is/content/discounttire/logos/treadwell-icon-only-trademark.svg"
                    />
                    <Typography tag="span" weight="bold">
                      Your Treadwell™ Profile
                    </Typography>
                  </>
                }
                toggleCollapsible={() => setIsOpen(!isOpen)}
                toggleType={'CHEVRON'}
              />
            ) : (
              <>
                <Typography tag="span" weight="bold">
                  Your Driving Profile
                </Typography>
                <Link
                  className={styles['edit']}
                  onClick={() => trackAction('vehicle_page_edit')}
                  size="small"
                  to="#/fitment/vehicle/edit/confirm"
                >
                  edit
                </Link>
              </>
            )}
          </header>

          <div className={styles['vehicle']}>
            <VehicleImage
              alt="vehicle"
              className={styles['vehicle-image']}
              smallImageUrl={smallImageUrl}
            />
            <div className={styles['vehicle-details']}>
              <VehicleDescription
                make={make}
                model={model}
                rideHeight={rideHeight}
                trim={trim}
                year={year}
              />
              {isFleetEnabled && (
                <VehicleLicenseVinDetails
                  license={fitment?.licensePlateNumber}
                  vin={fitment?.vin}
                />
              )}
            </div>
          </div>

          {!showTreadwellProfile && (
            <>
              {fitment && frontTireSize && (
                <div className={styles['size-container']}>
                  <div className={styles['size']}>
                    <FitmentSize
                      isOE={isOE}
                      sizeQualifier={sizeQualifier}
                      tireLocation={isStaggered ? 'front' : undefined}
                      tireSize={frontTireSize}
                      wheelDiameter={frontWheelDiameter}
                    />
                  </div>

                  {isStaggered &&
                    (Boolean(rearTireSize) || Boolean(rearWheelDiameter)) && (
                      <div className={styles['size']}>
                        <FitmentSize
                          isOE={isOE}
                          sizeQualifier={sizeQualifier}
                          tireLocation="rear"
                          tireSize={rearTireSize}
                          wheelDiameter={rearWheelDiameter}
                        />
                      </div>
                    )}
                </div>
              )}
            </>
          )}

          {showTreadwellProfile && (
            <>
              <Collapsible
                className={styles['persona']}
                hideToggle
                isOpen={isOpen}
              >
                <Typography className={styles['driver-label']} tag="div">
                  Driver Type
                </Typography>
                <Typography
                  className={cn(
                    styles['driver-value'],
                    isFleetTreadwell ? styles['fleet-company-name'] : ''
                  )}
                  tag="div"
                  weight="bold"
                >
                  {name && <TreadwellPersonaIcon iconName={icon} />}{' '}
                  {isFleetTreadwell ? companyDriverValue : driverValue}
                </Typography>
                <Typography className={styles['driver-label']} tag="div">
                  Driving Location
                </Typography>
                <Typography
                  className={styles['driver-value']}
                  tag="div"
                  weight="bold"
                >
                  <span className={styles['city']}>{city?.toLowerCase()}</span>,{' '}
                  {state}
                </Typography>
                {!(isFleetTreadwell || isAppBasedDriver) && (
                  <WinterToggle onToggle={onWinterToggle} />
                )}
              </Collapsible>

              <Button isFullWidth isSecondary onClick={handleEditVehicle}>
                edit my info
              </Button>
            </>
          )}
        </>
      ) : (
        <>
          <VehicleImage
            alt="no vehicle"
            className={styles['no-vehicle-image']}
          />
          <Typography className={styles['prompt']} weight="black">
            Tell us about your vehicle to ensure these products fit
          </Typography>
          <Button
            className={styles['get-started-btn']}
            href="#/fitment/vehicle"
            isFullWidth
            isSecondary
          >
            Get Started
          </Button>
        </>
      )}
    </div>
  );
}

export default ProductListDrivingProfile;
